/**
 * safeSpotDistance — order venues by how far they are, on the device.
 *
 * This module is the reason there is no `?lat=&lng=` on the Safe Spot API. The
 * proposer's position is used once, here, in memory, to sort a list that was
 * fetched without it. It is never sent anywhere, so no request log, error
 * report or analytics event can hold where the user was standing when they
 * picked a place to meet.
 *
 * The list is a curated partner directory rather than a world map, so sorting it
 * locally is also simply cheaper than a geospatial query per keystroke.
 */

const EARTH_RADIUS_M = 6371000;

const toRadians = (deg) => (deg * Math.PI) / 180;

/**
 * The [lng, lat] pair, from either the public serializer or a stored document.
 *
 * `toPublic()` emits a flat `coordinates` array. The admin list, and the map
 * fixtures written against the earlier API, wrap the same pair in GeoJSON
 * `location`. Reading both here is what lets the map consume the sanctioned
 * public shape without every caller knowing which envelope it was handed.
 */
export function spotLngLat(spot) {
  const pair = Array.isArray(spot?.coordinates) && spot.coordinates.length === 2
    ? spot.coordinates
    : spot?.location?.coordinates;
  if (!Array.isArray(pair) || pair.length !== 2) return null;
  const [lng, lat] = pair;
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return pair;
}

/**
 * Great-circle distance in metres between two [lng, lat] pairs.
 *
 * Haversine rather than a projected approximation: a city-scale list still spans
 * enough latitude for a flat-earth shortcut to reorder near-equal venues, and
 * "nearest" being visibly wrong is worse than the arithmetic being slightly
 * heavier on a list this size.
 */
export function distanceBetween(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return null;
  const [lngA, latA] = a;
  const [lngB, latB] = b;
  if (![lngA, latA, lngB, latB].every((n) => Number.isFinite(n))) return null;

  const dLat = toRadians(latB - latA);
  const dLng = toRadians(lngB - lngA);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(latA)) * Math.cos(toRadians(latB)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Venues ordered by distance from `origin`, each annotated with `distanceM`.
 *
 * With no usable origin the list comes back in its original order and every
 * `distanceM` is null. That is the honest outcome when location is denied or
 * unavailable — the picker then simply shows an unordered list of real venues,
 * rather than an arbitrary order presented as "nearest".
 */
export function sortSpotsByDistance(spots, origin) {
  const list = Array.isArray(spots) ? [...spots] : [];
  const hasOrigin =
    Array.isArray(origin) && origin.length === 2 && origin.every(Number.isFinite);

  const annotated = list.map((spot) => ({
    ...spot,
    distanceM: hasOrigin ? distanceBetween(origin, spotLngLat(spot)) : null,
  }));

  if (!hasOrigin) return annotated;

  return annotated.sort((a, b) => {
    // A venue with no usable coordinates sorts last rather than to the top,
    // which is where a null would land in a naive numeric compare.
    if (a.distanceM === null && b.distanceM === null) return 0;
    if (a.distanceM === null) return 1;
    if (b.distanceM === null) return -1;
    return a.distanceM - b.distanceM;
  });
}

/**
 * How far a Safe Spot can be and still count as "near you": 25 km, roughly one
 * metro area or a half-hour trip (owner decision Q-28). One number, so it can be
 * tuned without hunting for it.
 */
export const NEAR_SAFE_SPOT_M = 25000;

/**
 * Whether any spot from `sortSpotsByDistance` is within `radiusM`.
 *
 * A spot with no known distance never counts as near — with no position, that
 * is every spot, so this is false rather than a guess.
 */
export function hasSpotWithin(annotated, radiusM = NEAR_SAFE_SPOT_M) {
  return (
    Array.isArray(annotated) &&
    annotated.some((s) => Number.isFinite(s?.distanceM) && s.distanceM <= radiusM)
  );
}

/**
 * "850 m" / "1.2 km", matching the detail sheet.
 *
 * Metres below a kilometre, rounded to 10 m so the number does not imply a
 * precision the position never had; kilometres to one decimal above that.
 */
export function formatDistance(distanceM) {
  if (!Number.isFinite(distanceM)) return null;
  if (distanceM < 1000) return `${Math.round(distanceM / 10) * 10} m`;
  // A decimal stops helping past 100 km; the beta is worldwide, so a far spot
  // can be thousands of km away and reads better as "9,123 km".
  if (distanceM < 99950) return `${(distanceM / 1000).toFixed(1)} km`;
  return `${Math.round(distanceM / 1000).toLocaleString("en-US")} km`;
}
